/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import {
	Box,
	Paper,
	Group,
	Image,
	Text,
	Skeleton,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { useNavigate } from 'react-router';
import isNil from 'lodash/isNil';
import {
	useCallback,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

// UI local components
import {
	AddFiltersButton,
	Searchbar,
	Pagination,
} from '@components/atoms';
import {
	CardProject,
	MenuFilter,
} from '@components/molecules';

// Icons
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';

// Layouts
import { Header } from '@layouts/Header';

// Styles
import classes from './additionality-projects.module.css';

// I18n
import { useTranslation } from 'react-i18next';

import { useAdditionalityProjects } from '@features/catalogs/additionality-projects/list/api/get-additionality-projects-public';
import { useAdditionalityProject } from '@features/catalogs/additionality-projects/list/hooks/useAdditionalityProjects';

// Constants
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { PROJECT_TECHNOLOGIES } from '@config/constants/project-technologies';

// Helpers
import { useCountryCodes } from 'shared/api/get-country-codes';

// Types
import { TApiResponseAdditionalityProjectPopulated } from '@global-types/api/api';

// Types

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const AdditionalityProjectsPublic = () => {
	/* ********************************** Hooks ********************************* */

	// I18n
	const { t } = useTranslation();

	// State to manage overflow filters visibility
	const [isOverflowFilters] = useState(false);

	// State to manage scroll filters visibility
	const [showScrollFilters, setShowScrollFilters] =
		useState(false);

	// Routing
	const navigate = useNavigate();

	// Ref for the container element
	const containerRef = useRef<HTMLDivElement>(null);

	// Fetch country codes
	const { data: countryCodes } = useCountryCodes(
		'additionalities'
	);

	// Filter countries based on fetched country codes
	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? EUROPEAN_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	// Custom hook for additionality project logic
	const {
		handleChangePaginationSize,
		handleChangePagination,
		additionalityProjectsPage,
		additionalityProjectsPageSize,
		additionalityProjectsQueryParams,
		handleTechnologyFilter,
		handleCountryFilter,
		handleProjectNameFilter,
	} = useAdditionalityProject();

	// Hook to get the size of the container
	const { ref, width: widthContainer } = useElementSize();

	/* ****************************** SIDE-EFFECTS ****************************** */

	// Effect to handle overflow and scroll visibility
	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	/* ********************************** API ********************************* */

	// Fetch additionality projects data
	const {
		data: additionalityProjects,
		isFetching,
		total: totalItems,
		totalPages,
	} = useAdditionalityProjects(
		additionalityProjectsPage,
		additionalityProjectsPageSize,
		additionalityProjectsQueryParams
	);

	/* ******************************** CALLBACKS ******************************* */
	// Function to handle scroll behavior
	const handleScroll = useCallback((scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	}, []);

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.layout}>
			<Box className={classes.wrapper}>
				<Box className={classes.content}>
					<Header
						id={''}
						label={t('global.seekingFundingProjects')}
						user={null}
						onLogout={() => {}}
					/>
					<Box className={classes.body}>
						<Box className={classes.container} ref={ref}>
							<Paper className={classes.paper}>
								{/* Row filters */}
								<div className={classes.containerFilters}>
									<div className={classes.wrapperRowLeft}>
										{!isOverflowFilters &&
											showScrollFilters && (
												<Box
													className={
														classes.wrapperIconScrollToRight
													}
													variant="transparent"
													onClick={() => {
														handleScroll('left');
													}}
												>
													<Image
														className={
															classes.containerIconScrollToLeft
														}
														src={ArrowScrollIcon}
													/>
												</Box>
											)}
										<div
											ref={containerRef}
											className={classes.wrapperFilters}
										>
											<Group gap={'0px'}>
												<Box
													className={
														classes.containerFilters5
													}
												>
													<Box
														className={
															classes.spacerFilter
														}
													/>
													<Box
														className={
															classes.containerFilterAutocomplete
														}
													>
														<Searchbar
															hiddenFrom="xxl"
															placeholder={t(
																'global.search'
															)}
															size={'sm'}
															onChange={
																handleProjectNameFilter
															}
														/>
														<Searchbar
															visibleFrom="xxl"
															placeholder={t(
																'global.search'
															)}
															size={'lg'}
															onChange={
																handleProjectNameFilter
															}
														/>
													</Box>
													<Box
														className={
															classes.spacerFilter
														}
													/>
													<Box
														className={
															classes.containerFilterSelect
														}
													>
														<MenuFilter
															value={
																additionalityProjectsQueryParams[
																	'location.countryCode'
																]
																	? additionalityProjectsQueryParams[
																			'location.countryCode'
																		]['$in']
																	: []
															}
															isPersist
															onApply={
																handleCountryFilter
															}
															label={t('global.country')}
															size="lg"
															options={filteredCountries}
															placeholderSearch={t(
																'filters.placeholderSearch.searchCountry'
															)}
															placeholder={t(
																'filters.placeholders.selectCountry'
															)}
														/>
													</Box>
													<Box
														className={
															classes.spacerFilter
														}
													/>
													<Box
														className={
															classes.containerFilterSelect
														}
													>
														<MenuFilter
															isPersist
															value={
																additionalityProjectsQueryParams.technology
																	? additionalityProjectsQueryParams.technology
																	: []
															}
															onApply={
																handleTechnologyFilter
															}
															label="Project type"
															size="lg"
															options={
																PROJECT_TECHNOLOGIES
															}
															placeholderSearch={t(
																'filters.placeholderSearch.searchTechnology'
															)}
															placeholder={t(
																'filters.placeholders.selectTechnology'
															)}
														/>
													</Box>
													<Box
														className={
															classes.spacerFilter
														}
													/>
													<Box
														className={
															classes.containerFilterNumeric
														}
													>
														<AddFiltersButton>
															{t('global.addFilters')}
														</AddFiltersButton>
													</Box>
												</Box>
											</Group>
										</div>
										{!isOverflowFilters &&
											showScrollFilters && (
												<Box
													className={
														classes.wrapperIconScrollToRight
													}
													variant="transparent"
													onClick={() => {
														handleScroll('right');
													}}
												>
													<Image
														className={
															classes.containerIconScrollToRight
														}
														src={ArrowScrollIcon}
													/>
												</Box>
											)}
									</div>
								</div>

								<Box className={classes.contentCards}>
									{isFetching ? (
										<Box
											className={
												classes.containerSkeletons
											}
										>
											{Array.from(Array(5).keys()).map(
												(index) => (
													<Skeleton
														key={index}
														height={260}
														width={260}
														radius="md"
													/>
												)
											)}
										</Box>
									) : Array.isArray(
											additionalityProjects
									  ) &&
									  additionalityProjects.length > 0 ? (
										additionalityProjects.map(
											(
												project: TApiResponseAdditionalityProjectPopulated
											) => (
												<CardProject
													key={project.id}
													size={'responsive'}
													type={'view-additionality'}
													data={project}
													isAdded={false}
													tco2={2}
													onAdd={() => {}}
													onChangeTco2={() => {}}
													handleShowMoreDetails={() => {
														navigate(
															`/catalog-additionalities/${project.id}`
														);
													}}
													inactive={!project?.active}
												/>
											)
										)
									) : (
										<Box
											className={
												classes.containerEmptyList
											}
										>
											<Box
												className={
													classes.containerTextsNoRecords
												}
											>
												<Image
													src={NoRecordsIcon}
													className={
														classes.iconNoRecords
													}
												/>
												<>
													<Text
														className={
															classes.titleNoRecords
														}
													>
														{t(
															'global.datatable.noResultsFound'
														)}
													</Text>
													<Text
														className={
															classes.descriptionNoRecords
														}
													>
														{t(
															'global.datatable.noResultsFoundDescription'
														)}
													</Text>
												</>
											</Box>
										</Box>
									)}
								</Box>
							</Paper>
							{/* Pagination */}
							<Box
								className={classes.containerPagination}
							>
								<Pagination
									pagesize={additionalityProjectsPageSize}
									totalitems={totalItems}
									activepage={additionalityProjectsPage}
									handlechangesize={
										handleChangePaginationSize
									}
									onChange={handleChangePagination}
									total={totalPages ? totalPages : 0}
								/>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default AdditionalityProjectsPublic;
